import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  Row,
  Col,
  Card,
  Table,
  Tag,
  Descriptions,
  Progress,
  Spin,
  Empty,
} from "antd";
import { WarningOutlined, TrophyOutlined } from "@ant-design/icons";
import ReactECharts from "echarts-for-react";
import moment from "moment";
import supplierRankDashboardApis from "../../../../api/dashboard/supplierRankDashboard";

const num = (v: any, fallback = 0): number => {
  if (v === null || v === undefined || v === "") return fallback;
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const fmtDate = (v: any): string =>
  v ? moment.utc(v).format("DD-MMM-YYYY") : "—";

const formatINR = (val: number) => {
  if (val >= 10000000) return `${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `${(val / 100000).toFixed(2)} L`;
  if (val >= 1000) return `${(val / 1000).toFixed(1)} K`;
  return val.toFixed(0);
};

const getScoreColor = (score: number) => {
  if (score >= 80) return "#52c41a";
  if (score >= 60) return "#faad14";
  return "#ff4d4f";
};

const getScoreLabel = (score: number) => {
  if (score >= 85) return "Excellent";
  if (score >= 70) return "Good";
  if (score >= 55) return "Average";
  return "Poor";
};

const getRecommendationStyle = (score: number) => {
  if (score >= 80)
    return {
      bg: "#f6ffed",
      border: "#b7eb8f",
      icon: <TrophyOutlined style={{ color: "#52c41a", fontSize: 18 }} />,
    };
  if (score >= 60)
    return {
      bg: "#fffbe6",
      border: "#ffe58f",
      icon: <WarningOutlined style={{ color: "#faad14", fontSize: 18 }} />,
    };
  return {
    bg: "#fff2f0",
    border: "#ffccc7",
    icon: <WarningOutlined style={{ color: "#ff4d4f", fontSize: 18 }} />,
  };
};

const getRecommendationText = (score: number, name: string) => {
  if (score >= 80)
    return `Preferred Vendor - ${name} shows strong overall performance. Recommended for primary sourcing.`;
  if (score >= 60)
    return `Good Vendor - ${name} performs adequately. Monitor delivery and pricing trends before increasing volume.`;
  return `High Risk - ${name} shows poor performance metrics. Consider sourcing alternatives.`;
};

const avgScoreByDimension = (rows: any[], dimension: string): number => {
  if (!Array.isArray(rows)) return 0;
  const matches = rows.filter(
    (r) => (r?.dimension || "").toLowerCase() === dimension.toLowerCase()
  );
  if (!matches.length) return 0;
  const total = matches.reduce((acc, r) => acc + num(r.score), 0);
  return Math.round(total / matches.length);
};

const emptyData = (name: string) => ({
  name: name || "—",
  contact: "—",
  phone: "—",
  email: "—",
  gst: "—",
  category: "—",
  since: "—",
  purchaseSummary: {
    totalPOs: 0,
    totalValueDisplay: "—",
    avgPOValueDisplay: "—",
    totalQtyOrdered: 0,
    totalQtyDelivered: 0,
    fulfillmentPct: 0,
    materials: [] as any[],
    monthlyPurchase: {
      months: [] as string[],
      values: [] as number[],
      quantities: [] as number[],
    },
  },
  deliveryPatterns: {
    totalDeliveries: 0,
    onTime: 0,
    delayed: 0,
    avgLeadDays: 0,
    minLeadDays: 0,
    maxLeadDays: 0,
    onTimePercent: 0,
    avgDelayDays: 0,
    deliveryHistory: [] as any[],
    leadTimeTrend: { months: [] as string[], avgDays: [] as number[] },
  },
  scores: {
    overall: 0,
    pricing: 0,
    delivery: 0,
    quality: 0,
    responsiveness: 0,
    consistency: 0,
  },
  recommendation: "",
});

const buildData = (responses: any, fallbackName: string) => {
  const detailsList = responses?.details?.response?.data || [];
  const details = detailsList[0] || {};
  const breakdown = responses?.scoreBreakdown?.response?.data || [];
  const summaryRow = responses?.purchaseSummary?.response?.data?.[0] || {};
  const trend = responses?.purchaseTrend?.response?.data || [];
  const materials = responses?.materialSummary?.response?.data || [];
  const patterns = responses?.deliveryPatterns?.response?.data?.[0] || {};
  const dpSummary =
    responses?.deliveryPatternsSummary?.response?.data?.[0] || {};
  const leadTrend = responses?.leadTimeTrend?.response?.data || [];
  const poDetails = responses?.poDeliveryDetails?.response?.data || [];

  const overall = num(details.overallScore);
  const name = details.supplierName || fallbackName || "—";

  return {
    name,
    contact: details.contactPerson || "—",
    phone: details.phoneNumber || "—",
    email: details.email || "—",
    gst: details.gstin || "—",
    category: details.categoryName || "—",
    since: details.supplierSince ? fmtDate(details.supplierSince) : "—",
    purchaseSummary: {
      totalPOs: num(summaryRow.totalPOs),
      totalValueDisplay: summaryRow.totalValue || "—",
      avgPOValueDisplay: summaryRow.avgPOValue || "—",
      totalQtyOrdered: num(summaryRow.qtyOrdered),
      totalQtyDelivered: num(summaryRow.qtyDelivered),
      fulfillmentPct: num(summaryRow.fulfillmentPct),
      materials: materials.map((m: any) => ({
        material: m.material,
        poCount: num(m.POs),
        totalQty: num(m.totalQty),
        totalValueDisplay: m.totalValue || "—",
        avgPriceDisplay: m.avgPrice || "—",
      })),
      monthlyPurchase: {
        months: trend.map((t: any) => `${t.monthName} ${t.year}`),
        values: trend.map((t: any) => num(t.value)),
        quantities: trend.map((t: any) => num(t.quantity)),
      },
    },
    deliveryPatterns: {
      totalDeliveries: num(patterns.totalDelivered),
      onTime: num(patterns.onTimeCount),
      delayed: num(patterns.delayedCount),
      avgLeadDays: num(dpSummary.avgLeadDays),
      minLeadDays: num(dpSummary.minLeadDays),
      maxLeadDays: num(dpSummary.maxLeadDays),
      onTimePercent: num(dpSummary.onTimePct ?? patterns.onTimePct),
      avgDelayDays: num(dpSummary.avgDelayDays),
      deliveryHistory: poDetails.map((p: any) => ({
        poNumber: p.poNumber || `#${p.orderNumber}`,
        poDate: fmtDate(p.poDate),
        expectedDate: fmtDate(p.expectedDate),
        actualDate: p.actualDate ? fmtDate(p.actualDate) : "—",
        daysVariance: p.varianceDays,
        status: p.deliveryStatus || "—",
      })),
      leadTimeTrend: {
        months: leadTrend.map((t: any) =>
          t.monthName && t.year ? `${t.monthName} ${t.year}` : t.month || ""
        ),
        avgDays: leadTrend.map((t: any) =>
          num(t.avgDays ?? t.avgLeadDays ?? t.value)
        ),
      },
    },
    scores: {
      overall,
      pricing: avgScoreByDimension(breakdown, "Pricing"),
      delivery: avgScoreByDimension(breakdown, "Delivery"),
      quality: avgScoreByDimension(breakdown, "Quality"),
      responsiveness: avgScoreByDimension(breakdown, "Responsiveness"),
      consistency: avgScoreByDimension(breakdown, "Consistency"),
    },
    recommendation: getRecommendationText(overall, name),
  };
};

const PurchaseHistoryDrawer: React.FC<any> = ({
  paramsData,
  visible,
  onClose,
}) => {
  const vendorName = paramsData?.supplierName || "";
  const supplierId = paramsData?.supplierId;
  const pageSize = paramsData?.pageSize || 10;

  const [chartsReady, setChartsReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [responses, setResponses] = useState<any>(null);

  useEffect(() => {
    if (!visible || !supplierId) return;
    let cancelled = false;

    const baseParams = { supplierIds: String(supplierId) };
    const trendParams = { ...baseParams, pageSize: 24, page: 1 };
    const breakdownParams = { ...baseParams, pageSize: 500, page: 1 };
    const materialParams = { ...baseParams, pageSize: 50, page: 1 };
    const poParams = { ...baseParams, pageSize, page: 1 };

    setLoading(true);
    Promise.all([
      supplierRankDashboardApis.supplierDetails(baseParams),
      supplierRankDashboardApis.scoreBreakdown(breakdownParams),
      supplierRankDashboardApis.purchaseSummary(baseParams),
      supplierRankDashboardApis.purchaseTrend(trendParams),
      supplierRankDashboardApis.materialSummary(materialParams),
      supplierRankDashboardApis.deliveryPatterns(baseParams),
      supplierRankDashboardApis.deliveryPatternsSummary(baseParams),
      supplierRankDashboardApis.leadTimeTrend(trendParams),
      supplierRankDashboardApis.poDeliveryDetails(poParams),
    ])
      .then(
        ([
          details,
          scoreBreakdown,
          purchaseSummary,
          purchaseTrend,
          materialSummary,
          deliveryPatterns,
          deliveryPatternsSummary,
          leadTimeTrend,
          poDeliveryDetails,
        ]) => {
          if (cancelled) return;
          setResponses({
            details,
            scoreBreakdown,
            purchaseSummary,
            purchaseTrend,
            materialSummary,
            deliveryPatterns,
            deliveryPatternsSummary,
            leadTimeTrend,
            poDeliveryDetails,
          });
        }
      )
      .catch((err) => {
        console.error("supplierRankDashboard fetch failed", err);
        if (!cancelled) setResponses(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [visible, supplierId, pageSize]);

  const data = useMemo(
    () =>
      responses ? buildData(responses, vendorName) : emptyData(vendorName),
    [responses, vendorName]
  );

  const recStyle = getRecommendationStyle(data.scores.overall);

  const purchaseTrendOption = {
    tooltip: {
      trigger: "axis",
      formatter: (params: any) => {
        let html = `<strong>${params[0].axisValue}</strong><br/>`;
        params.forEach((p: any) => {
          if (p.seriesName === "Value")
            html += `${p.marker} ${p.seriesName}: INR ${formatINR(p.value)}<br/>`;
          else html += `${p.marker} ${p.seriesName}: ${p.value}<br/>`;
        });
        return html;
      },
    },
    legend: { data: ["Value", "Quantity"], bottom: 0 },
    grid: { left: "3%", right: "4%", bottom: "14%", top: "14%", containLabel: true },
    xAxis: {
      type: "category",
      data: data.purchaseSummary.monthlyPurchase.months,
      axisLabel: { fontSize: 10 },
    },
    yAxis: [
      {
        type: "value",
        name: "Value (INR)",
        nameTextStyle: { fontSize: 10, padding: [0, 0, 0, 40] },
        nameGap: 10,
        axisLabel: { formatter: (v: number) => formatINR(v), fontSize: 10 },
      },
      {
        type: "value",
        name: "Qty",
        nameTextStyle: { fontSize: 10, padding: [0, 40, 0, 0] },
        nameGap: 10,
        axisLabel: { fontSize: 10 },
      },
    ],
    series: [
      {
        name: "Value",
        type: "bar",
        data: data.purchaseSummary.monthlyPurchase.values,
        itemStyle: { color: "#5470c6", borderRadius: [4, 4, 0, 0] },
      },
      {
        name: "Quantity",
        type: "line",
        yAxisIndex: 1,
        data: data.purchaseSummary.monthlyPurchase.quantities,
        itemStyle: { color: "#91cc75" },
        lineStyle: { width: 2 },
        symbolSize: 6,
      },
    ],
  };

  const leadTimeTrendOption = {
    tooltip: { trigger: "axis" },
    grid: { left: "3%", right: "4%", bottom: "8%", top: "8%", containLabel: true },
    xAxis: {
      type: "category",
      boundaryGap: false,
      data: data.deliveryPatterns.leadTimeTrend.months,
      axisLabel: { fontSize: 10 },
    },
    yAxis: { type: "value", name: "Days", axisLabel: { fontSize: 10 } },
    series: [
      {
        name: "Avg Lead Time",
        type: "line",
        data: data.deliveryPatterns.leadTimeTrend.avgDays,
        smooth: true,
        lineStyle: { width: 2 },
        itemStyle: { color: "#ee6666" },
        areaStyle: { opacity: 0.1 },
        markLine: {
          data: [
            {
              type: "average",
              label: { formatter: "Avg: {c} days" },
              lineStyle: { color: "#faad14", type: "dashed" },
            },
          ],
        },
      },
    ],
  };

  const scoreRadarOption = {
    radar: {
      indicator: [
        { name: "Pricing", max: 100 },
        { name: "Delivery", max: 100 },
        { name: "Quality", max: 100 },
        { name: "Responsiveness", max: 100 },
        { name: "Consistency", max: 100 },
      ],
      radius: "65%",
    },
    series: [
      {
        type: "radar",
        data: [
          {
            value: [
              data.scores.pricing,
              data.scores.delivery,
              data.scores.quality,
              data.scores.responsiveness,
              data.scores.consistency,
            ],
            name: data.name,
            areaStyle: { opacity: 0.2 },
            lineStyle: { width: 2 },
            itemStyle: { color: getScoreColor(data.scores.overall) },
          },
        ],
      },
    ],
  };

  const deliveryPieOption = {
    tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)" },
    legend: {
      bottom: 0,
      data: ["On-Time", "Delayed"],
      itemWidth: 12,
      itemHeight: 12,
      textStyle: { fontSize: 11 },
    },
    series: [
      {
        type: "pie",
        radius: ["45%", "70%"],
        center: ["50%", "42%"],
        data: [
          { name: "On-Time", value: data.deliveryPatterns.onTime, itemStyle: { color: "#91cc75" } },
          { name: "Delayed", value: data.deliveryPatterns.delayed, itemStyle: { color: "#ee6666" } },
        ],
        label: {
          show: true,
          position: "inside",
          formatter: "{c}",
          fontSize: 13,
          fontWeight: 700,
          color: "#fff",
        },
        itemStyle: { borderRadius: 4, borderColor: "#fff", borderWidth: 2 },
      },
    ],
  };

  const materialColumns = [
    {
      title: "Material",
      dataIndex: "material",
      key: "material",
      render: (t: string) => <span style={{ fontWeight: 500 }}>{t}</span>,
    },
    { title: "POs", dataIndex: "poCount", key: "poCount", align: "center" as const },
    {
      title: "Total Qty",
      dataIndex: "totalQty",
      key: "totalQty",
      align: "right" as const,
      render: (v: number) => (v ? v.toLocaleString() : "—"),
    },
    {
      title: "Total Value",
      dataIndex: "totalValueDisplay",
      key: "totalValueDisplay",
      align: "right" as const,
    },
    {
      title: "Avg Price",
      dataIndex: "avgPriceDisplay",
      key: "avgPriceDisplay",
      align: "right" as const,
    },
  ];

  const deliveryColumns = [
    {
      title: "PO #",
      dataIndex: "poNumber",
      key: "poNumber",
      render: (t: string) => <span style={{ fontWeight: 500 }}>{t}</span>,
    },
    { title: "PO Date", dataIndex: "poDate", key: "poDate" },
    { title: "Expected", dataIndex: "expectedDate", key: "expectedDate" },
    { title: "Actual", dataIndex: "actualDate", key: "actualDate" },
    {
      title: "Variance",
      dataIndex: "daysVariance",
      key: "daysVariance",
      align: "center" as const,
      render: (val: number | null) => {
        if (val === null || val === undefined)
          return <span style={{ color: "#8094ae" }}>—</span>;
        const color = val <= 0 ? "#52c41a" : val <= 2 ? "#faad14" : "#ff4d4f";
        return (
          <span style={{ color, fontWeight: 600 }}>
            {val > 0 ? `+${val}` : val} days
          </span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center" as const,
      render: (val: string) => {
        const colors: Record<string, string> = {
          Early: "green",
          "On-Time": "blue",
          OnTime: "blue",
          Delivered: "green",
          Delayed: "red",
          Pending: "orange",
        };
        return <Tag color={colors[val] || "default"}>{val}</Tag>;
      },
    },
  ];

  const scoreItems = [
    { label: "Pricing", value: data.scores.pricing },
    { label: "Delivery", value: data.scores.delivery },
    { label: "Quality", value: data.scores.quality },
    { label: "Responsiveness", value: data.scores.responsiveness },
    { label: "Consistency", value: data.scores.consistency },
  ];

  const fulfillmentPct = data.purchaseSummary.fulfillmentPct
    ? Math.round(data.purchaseSummary.fulfillmentPct)
    : data.purchaseSummary.totalQtyOrdered
    ? Math.round(
        (data.purchaseSummary.totalQtyDelivered /
          data.purchaseSummary.totalQtyOrdered) *
          100
      )
    : 0;

  return (
    <Drawer
      destroyOnClose
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>{data.name}</span>
          <Tag
            color={getScoreColor(data.scores.overall)}
            style={{ fontSize: 13, padding: "2px 10px" }}
          >
            Score: {data.scores.overall}/100 — {getScoreLabel(data.scores.overall)}
          </Tag>
        </div>
      }
      open={visible}
      onClose={onClose}
      afterOpenChange={(open) => setChartsReady(open)}
      width={960}
      bodyStyle={{ padding: 16, background: "#f5f6fa" }}
    >
      <Spin spinning={loading}>
        <div
          style={{
            padding: "12px 16px",
            background: recStyle.bg,
            border: `1px solid ${recStyle.border}`,
            borderRadius: 8,
            marginBottom: 16,
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
          }}
        >
          {recStyle.icon}
          <div>
            <div style={{ fontWeight: 600, color: "#364a63", marginBottom: 2 }}>
              Decision Recommendation
            </div>
            <div style={{ fontSize: 13, color: "#526484" }}>
              {data.recommendation || "—"}
            </div>
          </div>
        </div>

        <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
          <Col xs={24} lg={14}>
            <Card size="small" bodyStyle={{ padding: 12 }}>
              <Descriptions
                column={2}
                size="small"
                title={
                  <span style={{ fontSize: 13, fontWeight: 600 }}>
                    Supplier Details
                  </span>
                }
              >
                <Descriptions.Item label="Contact">{data.contact}</Descriptions.Item>
                <Descriptions.Item label="Phone">{data.phone}</Descriptions.Item>
                <Descriptions.Item label="Email">{data.email}</Descriptions.Item>
                <Descriptions.Item label="GST">{data.gst}</Descriptions.Item>
                <Descriptions.Item label="Category">{data.category}</Descriptions.Item>
                <Descriptions.Item label="Supplier Since">{data.since}</Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col xs={24} lg={10}>
            <Card size="small" bodyStyle={{ padding: 12, textAlign: "center" }}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: "#364a63",
                }}
              >
                Overall Supplier Score
              </div>
              <Progress
                type="dashboard"
                percent={data.scores.overall}
                strokeColor={getScoreColor(data.scores.overall)}
                format={(p) => (
                  <span
                    style={{
                      fontSize: 22,
                      fontWeight: 700,
                      color: getScoreColor(data.scores.overall),
                    }}
                  >
                    {p}
                  </span>
                )}
                size={120}
              />
              <div
                style={{
                  marginTop: 4,
                  fontSize: 14,
                  fontWeight: 600,
                  color: getScoreColor(data.scores.overall),
                }}
              >
                {getScoreLabel(data.scores.overall)}
              </div>
            </Card>
          </Col>
        </Row>

        <Card size="small" bodyStyle={{ padding: 12 }} style={{ marginBottom: 16 }}>
          <Row gutter={[12, 12]}>
            <Col xs={24} lg={12}>
              <div
                style={{
                  fontSize: 13,
                  fontWeight: 600,
                  marginBottom: 8,
                  color: "#364a63",
                }}
              >
                Score Breakdown
              </div>
              {scoreItems.map((s) => (
                <div
                  key={s.label}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 6,
                  }}
                >
                  <span style={{ width: 110, fontSize: 12, color: "#526484" }}>
                    {s.label}
                  </span>
                  <Progress
                    percent={s.value}
                    size="small"
                    strokeColor={getScoreColor(s.value)}
                    style={{ flex: 1 }}
                    format={(p) => (
                      <span style={{ fontSize: 11, fontWeight: 600 }}>{p}</span>
                    )}
                  />
                </div>
              ))}
            </Col>
            <Col xs={24} lg={12}>
              {chartsReady && (
                <ReactECharts
                  option={scoreRadarOption}
                  style={{ height: 220, width: "100%" }}
                  notMerge
                />
              )}
            </Col>
          </Row>
        </Card>

        <Card size="small" bodyStyle={{ padding: 12 }} style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 8,
              color: "#364a63",
            }}
          >
            Purchase Summary
          </div>
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={8} sm={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#8094ae" }}>Total POs</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#364a63" }}>
                  {data.purchaseSummary.totalPOs}
                </div>
              </div>
            </Col>
            <Col xs={8} sm={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#8094ae" }}>Total Value</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#364a63" }}>
                  {data.purchaseSummary.totalValueDisplay}
                </div>
              </div>
            </Col>
            <Col xs={8} sm={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#8094ae" }}>Avg PO Value</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#364a63" }}>
                  {data.purchaseSummary.avgPOValueDisplay}
                </div>
              </div>
            </Col>
            <Col xs={8} sm={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#8094ae" }}>Qty Ordered</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#364a63" }}>
                  {data.purchaseSummary.totalQtyOrdered.toLocaleString()}
                </div>
              </div>
            </Col>
            <Col xs={8} sm={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#8094ae" }}>Qty Delivered</div>
                <div style={{ fontSize: 16, fontWeight: 700, color: "#52c41a" }}>
                  {data.purchaseSummary.totalQtyDelivered.toLocaleString()}
                </div>
              </div>
            </Col>
            <Col xs={8} sm={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#8094ae" }}>Fulfillment</div>
                <div
                  style={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: getScoreColor(fulfillmentPct),
                  }}
                >
                  {fulfillmentPct}%
                </div>
              </div>
            </Col>
          </Row>
          {chartsReady && data.purchaseSummary.monthlyPurchase.months.length > 0 && (
            <ReactECharts
              option={purchaseTrendOption}
              style={{ height: 220, width: "100%" }}
              notMerge
            />
          )}
          <div style={{ marginTop: 12 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 600,
                marginBottom: 6,
                color: "#526484",
              }}
            >
              Material-wise Breakdown
            </div>
            <Table
              columns={materialColumns}
              dataSource={data.purchaseSummary.materials}
              rowKey={(record: any, i?: number) => `${record.material}-${i}`}
              size="small"
              pagination={false}
              locale={{
                emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No materials" />,
              }}
            />
          </div>
        </Card>

        <Card size="small" bodyStyle={{ padding: 12 }} style={{ marginBottom: 16 }}>
          <div
            style={{
              fontSize: 13,
              fontWeight: 600,
              marginBottom: 8,
              color: "#364a63",
            }}
          >
            Delivery Patterns
          </div>
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={24} lg={8}>
              {chartsReady && (
                <ReactECharts
                  option={deliveryPieOption}
                  style={{ height: 180, width: "100%" }}
                  notMerge
                />
              )}
            </Col>
            <Col xs={24} lg={16}>
              {chartsReady && data.deliveryPatterns.leadTimeTrend.months.length > 0 && (
                <ReactECharts
                  option={leadTimeTrendOption}
                  style={{ height: 180, width: "100%" }}
                  notMerge
                />
              )}
            </Col>
          </Row>
          <Row gutter={[12, 12]} style={{ marginBottom: 12 }}>
            <Col xs={8} sm={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#8094ae" }}>Avg Lead</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#364a63" }}>
                  {data.deliveryPatterns.avgLeadDays} days
                </div>
              </div>
            </Col>
            <Col xs={8} sm={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#8094ae" }}>Min</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#52c41a" }}>
                  {data.deliveryPatterns.minLeadDays} days
                </div>
              </div>
            </Col>
            <Col xs={8} sm={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#8094ae" }}>Max</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#ff4d4f" }}>
                  {data.deliveryPatterns.maxLeadDays} days
                </div>
              </div>
            </Col>
            <Col xs={8} sm={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#8094ae" }}>On-Time</div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color: getScoreColor(data.deliveryPatterns.onTimePercent),
                  }}
                >
                  {data.deliveryPatterns.onTimePercent}%
                </div>
              </div>
            </Col>
            <Col xs={8} sm={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#8094ae" }}>Avg Delay</div>
                <div
                  style={{
                    fontSize: 14,
                    fontWeight: 700,
                    color:
                      data.deliveryPatterns.avgDelayDays > 2 ? "#ff4d4f" : "#faad14",
                  }}
                >
                  {data.deliveryPatterns.avgDelayDays} days
                </div>
              </div>
            </Col>
            <Col xs={8} sm={4}>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 11, color: "#8094ae" }}>Delayed</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#ff4d4f" }}>
                  {data.deliveryPatterns.delayed}
                </div>
              </div>
            </Col>
          </Row>
          <Table
            columns={deliveryColumns}
            dataSource={data.deliveryPatterns.deliveryHistory}
            rowKey={(record: any, i?: number) => `${record.poNumber}-${i}`}
            size="small"
            pagination={false}
            locale={{
              emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No PO deliveries" />,
            }}
          />
        </Card>
      </Spin>
    </Drawer>
  );
};

export default PurchaseHistoryDrawer;
